import React, { useRef, useState } from "react";
import { Upload, Loader2 } from "lucide-react";
import AssetCard from "./AssetCard";

interface AssetGridProps {
  assets: BrandAsset[];
  assetType: BrandAssetType;
  onUpload: (file: File) => Promise<void>;
  onDelete: (assetId: string) => void;
  onUpdate: (assetId: string, updates: FormData) => void;
  renderMeta?: (asset: BrandAsset) => React.ReactNode;
  emptyMessage?: string;
}

const AssetGrid: React.FC<AssetGridProps> = ({
  assets,
  assetType,
  onUpload,
  onDelete,
  onUpdate,
  renderMeta,
  emptyMessage = "No assets yet. Upload one to get started.",
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

  const handleFiles = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    const oversized = Array.from(files).find((f) => f.size > MAX_FILE_SIZE);
    if (oversized) {
      setUploadError(`File "${oversized.name}" exceeds 10MB limit.`);
      return;
    }
    setIsUploading(true);
    setUploadError(null);
    try {
      for (const file of Array.from(files)) {
        await onUpload(file);
      }
    } catch {
      setUploadError("Upload failed. Please try again.");
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    handleFiles(e.dataTransfer.files);
  };

  return (
    <div>
      {/* Upload zone */}
      <div
        className={`mb-4 border-2 border-dashed rounded-lg p-6 text-center transition-colors cursor-pointer ${
          dragOver ? "border-blue-500 bg-blue-500/10" : "border-gray-700 hover:border-gray-500"
        }`}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="image/png,image/jpeg,image/webp,image/svg+xml"
          multiple
          className="hidden"
          onChange={(e) => handleFiles(e.target.files)}
        />
        {isUploading ? (
          <div className="flex items-center justify-center gap-2 text-blue-400">
            <Loader2 className="w-5 h-5 animate-spin" />
            <span>Uploading...</span>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-2 text-gray-400">
            <Upload className="w-8 h-8" />
            <span className="text-sm">Drop files here or click to upload</span>
            <span className="text-xs text-gray-500">PNG, JPG, WebP, SVG (max 10MB)</span>
          </div>
        )}
      </div>

      {uploadError && (
        <div className="mb-4 text-red-400 text-sm bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2 flex items-center justify-between">
          <span>{uploadError}</span>
          <button onClick={() => setUploadError(null)} className="text-red-400 hover:text-red-300 ml-2">✕</button>
        </div>
      )}

      {/* Grid */}
      {assets.length > 0 ? (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {assets.map((asset) => (
            <AssetCard
              key={asset.id}
              asset={asset}
              onDelete={onDelete}
              onUpdate={onUpdate}
              renderMeta={renderMeta}
            />
          ))}
        </div>
      ) : (
        <div className="text-gray-500 text-sm text-center py-8">{emptyMessage}</div>
      )}
    </div>
  );
};

export default AssetGrid;
